import { useZero } from '@rocicorp/zero/react';
import type { Zero } from '@rocicorp/zero';
import type { Schema } from '@strength/zero-schema';
import type { FeatureFlags } from '@strength/utils';
import { useFlags } from '../context/featureFlag';

// Sentinel Zero instance published by ZeroClientProvider (via ZeroContext) while
// the *real* ZeroProvider isn't active. Keeps useZero() from throwing when the
// provider tree is mounted but Zero is gated off — its internals are never
// reached because useIsZeroActive() reports false against it.
// SAFETY: no Zero client exists yet — the object identity (vs ZERO_STUB) is the
// only thing read from this value; its internals are never accessed.
export const ZERO_STUB = {} as unknown as Zero<Schema>;

// True only while the real ZeroProvider (with the auth `context`) is mounted —
// equivalent to "ZeroContext holds a real instance, not the stub", which folds
// in all of ZeroClientProvider's gates (flag/debug toggle, cache URL, org
// session). Before an org session resolves — e.g. on first login — only the
// stub is mounted.
//
// Query hooks must gate query execution on this signal, NOT on the raw
// LaunchDarkly `zeroFlag`: the flag can be on while the real provider isn't yet
// mounted, and running a synced query against the contextless stub passes
// `ctx === undefined` into the query fn and crashes (SC-3525).
export function useIsZeroActive(): boolean {
  return useZero<Schema>() !== ZERO_STUB;
}

// Persisted override for the local Zero toggle (debug tooling). Device-scoped,
// not per-user: it governs whether this client mounts Zero at all, independent
// of who is signed in. Best-effort — localStorage may be unavailable.
export const ZERO_ENABLED_STORAGE_KEY = 'zero:debug-enabled';

export function readStoredZeroEnabled(): boolean | null {
  try {
    const stored = localStorage.getItem(ZERO_ENABLED_STORAGE_KEY);
    return stored === null ? null : stored === 'true';
  } catch {
    return null;
  }
}

// Surface-scoped Zero rollout flags (Zero Migration Phase 3). A new surface
// appends one entry here and one flag in temporaryFeatureFlagsSchema.
export const SURFACE_ZERO_FLAGS = {
  tracks: 'tracksZeroFlag',
  plan: 'planZeroFlag',
  actuals: 'actualsZeroFlag',
  blockBuilder: 'blockBuilderZeroFlag',
  publishPrint: 'publishPrintZeroFlag',
  tablet: 'tabletZeroFlag',
} as const satisfies Record<string, keyof FeatureFlags>;

export type ZeroSurface = keyof typeof SURFACE_ZERO_FLAGS;

// True when the real ZeroProvider is mounted AND Zero is enabled for the
// surface: `zeroFlag`, the debug-drawer override, or the surface's own flag.
// `zeroFlag` and the override override up — they never turn a surface off.
// Dual-path call sites in a migrating surface switch their data fetching on
// this signal (see SC-5501 for tracks).
export function useIsZeroSurfaceActive(surface: ZeroSurface): boolean {
  const isZeroActive = useIsZeroActive();
  const flags = useFlags();
  const overrideEnabled = readStoredZeroEnabled() ?? false;
  return (
    isZeroActive &&
    (overrideEnabled || flags.zeroFlag || flags[SURFACE_ZERO_FLAGS[surface]])
  );
}
